import { useState, useEffect, useCallback } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { Menu, X, Zap } from 'lucide-react'
import {
  slideInLeft,
  staggerContainer,
  staggerItem,
  springSnappy,
  EASE_OUT,
} from '@/utils/animations'

// ─── Data ─────────────────────────────────────────────────────────────────────

const navLinks = [
  { label: 'Home',      href: '/' },
  { label: 'Dashboard', href: '/dashboard' },
  { label: 'Profile',   href: '/profile' },
  { label: 'Payment',   href: '/payment' },
]

// ─── Animation variants ───────────────────────────────────────────────────────

const mobileMenuVariants = {
  initial: { opacity: 0, height: 0, y: -8 },
  animate: {
    opacity: 1,
    height: 'auto',
    y: 0,
    transition: { duration: 0.28, ease: EASE_OUT },
  },
  exit: {
    opacity: 0,
    height: 0,
    y: -4,
    transition: { duration: 0.22, ease: [0.55, 0.06, 0.68, 0.19] },
  },
}

// Mobile link rows stagger inward from the left
const mobileLinkContainer = {
  initial: {},
  animate: {
    transition: { staggerChildren: 0.055, delayChildren: 0.06 },
  },
  exit: {
    transition: { staggerChildren: 0.03, staggerDirection: -1 },
  },
}

const mobileLinkItem = {
  initial: { opacity: 0, x: -18 },
  animate: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.25, ease: EASE_OUT },
  },
  exit: {
    opacity: 0,
    x: -12,
    transition: { duration: 0.15 },
  },
}

// Icon swap inside hamburger button
const iconVariants = {
  initial: { opacity: 0, rotate: -45, scale: 0.7 },
  animate: {
    opacity: 1,
    rotate: 0,
    scale: 1,
    transition: { duration: 0.2, ease: EASE_OUT },
  },
  exit: {
    opacity: 0,
    rotate: 45,
    scale: 0.7,
    transition: { duration: 0.15 },
  },
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function Navbar() {
  const [isOpen,   setIsOpen]   = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location   = useLocation()
  const navigate   = useNavigate()
  const shouldReduceMotion = useReducedMotion()

  // ── Scroll watcher ──────────────────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // ── Close mobile menu on route change ───────────────────────────────────────
  useEffect(() => { setIsOpen(false) }, [location.pathname])

  // ── Smooth-scroll helper for same-page anchor links ─────────────────────────
  const handleNavClick = useCallback((href) => {
    if (href.startsWith('#')) {
      const el = document.querySelector(href)
      el?.scrollIntoView({ behavior: shouldReduceMotion ? 'auto' : 'smooth' })
    }
  }, [shouldReduceMotion])

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0,   opacity: 1 }}
      transition={{ duration: 0.5, ease: EASE_OUT, delay: 0.05 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'glass-strong border-b border-border-subtle shadow-card'
          : 'bg-transparent'
      }`}
    >
      <div className="container-app">
        <div className="flex items-center justify-between h-16">

          {/* ── Logo ─────────────────────────────────────────────────────── */}
          <Link to="/" className="flex items-center gap-2 group">
            <motion.div
              whileHover={!shouldReduceMotion ? { scale: 1.08, rotate: -6 } : undefined}
              transition={springSnappy}
              className="w-8 h-8 rounded-lg bg-gradient-primary flex items-center justify-center shadow-glow-sm group-hover:shadow-glow transition-shadow duration-300"
            >
              <Zap className="w-4 h-4 text-white" />
            </motion.div>
            <span className="font-bold text-lg text-gradient">Portfolio</span>
          </Link>

          {/* ── Desktop Nav ──────────────────────────────────────────────── */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.href
              return (
                <motion.div
                  key={link.href}
                  whileHover={!shouldReduceMotion ? { scale: 1.04 } : undefined}
                  whileTap={!shouldReduceMotion  ? { scale: 0.97 } : undefined}
                  transition={springSnappy}
                >
                  <Link
                    to={link.href}
                    onClick={() => handleNavClick(link.href)}
                    className={`relative block px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                      isActive
                        ? 'text-primary-300'
                        : 'text-text-muted hover:text-text'
                    }`}
                  >
                    {isActive && (
                      <motion.span
                        layoutId="nav-active"
                        className="absolute inset-0 bg-primary-500/10 rounded-lg border border-primary-500/20"
                        transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                      />
                    )}
                    <span className="relative">{link.label}</span>
                  </Link>
                </motion.div>
              )
            })}
          </nav>

          {/* ── Desktop CTA ──────────────────────────────────────────────── */}
          <div className="hidden md:flex items-center gap-3">
            <motion.div
              whileHover={!shouldReduceMotion ? { scale: 1.03 } : undefined}
              whileTap={!shouldReduceMotion   ? { scale: 0.97 } : undefined}
              transition={springSnappy}
            >
              <Link
                to="/login"
                className="px-4 py-2 text-sm font-medium text-text-muted hover:text-text transition-colors duration-200"
              >
                Sign in
              </Link>
            </motion.div>

            <motion.button
              whileHover={!shouldReduceMotion ? { scale: 1.04, filter: 'brightness(1.12)' } : undefined}
              whileTap={!shouldReduceMotion   ? { scale: 0.96 } : undefined}
              transition={springSnappy}
              onClick={() => navigate('/register')}
              className="px-4 py-2 text-sm font-semibold bg-primary-500 hover:bg-primary-600 text-white rounded-lg shadow-glow-sm hover:shadow-glow transition-all duration-200"
            >
              Get Started
            </motion.button>
          </div>

          {/* ── Mobile hamburger ─────────────────────────────────────────── */}
          <motion.button
            whileTap={!shouldReduceMotion ? { scale: 0.88 } : undefined}
            transition={springSnappy}
            onClick={() => setIsOpen((prev) => !prev)}
            className="md:hidden p-2 rounded-lg text-text-muted hover:text-text hover:bg-surface transition-colors"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isOpen}
          >
            {/* Animate icon swap so open/close feels tactile */}
            <AnimatePresence mode="wait" initial={false}>
              {isOpen ? (
                <motion.span
                  key="close"
                  variants={!shouldReduceMotion ? iconVariants : undefined}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  style={{ display: 'block' }}
                >
                  <X className="w-5 h-5" />
                </motion.span>
              ) : (
                <motion.span
                  key="open"
                  variants={!shouldReduceMotion ? iconVariants : undefined}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  style={{ display: 'block' }}
                >
                  <Menu className="w-5 h-5" />
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </div>

      {/* ── Mobile Menu ────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="mobile-menu"
            variants={!shouldReduceMotion ? mobileMenuVariants : undefined}
            initial="initial"
            animate="animate"
            exit="exit"
            className="md:hidden overflow-hidden glass-strong border-t border-border-subtle"
          >
            <motion.nav
              className="container-app py-4 flex flex-col gap-1"
              variants={!shouldReduceMotion ? mobileLinkContainer : undefined}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              {navLinks.map((link) => (
                <motion.div
                  key={link.href}
                  variants={!shouldReduceMotion ? mobileLinkItem : undefined}
                >
                  <Link
                    to={link.href}
                    onClick={() => handleNavClick(link.href)}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === link.href
                        ? 'bg-primary-500/10 text-primary-300 border border-primary-500/20'
                        : 'text-text-muted hover:text-text hover:bg-surface'
                    }`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}

              {/* Auth buttons */}
              <motion.div
                variants={!shouldReduceMotion ? mobileLinkItem : undefined}
                className="mt-3 pt-3 border-t border-border-subtle flex flex-col gap-2"
              >
                <Link
                  to="/login"
                  className="px-4 py-3 text-sm font-medium text-text-muted hover:text-text text-center rounded-lg hover:bg-surface transition-colors"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-3 text-sm font-semibold bg-primary-500 text-white text-center rounded-lg"
                >
                  Get Started
                </Link>
              </motion.div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  )
}
